import React, { useState, useMemo, useCallback } from 'react';
import { useSelector } from 'react-redux';
import { Formik, Form, FormikErrors, FormikProps } from 'formik';
import { CopyIcon } from '@rsos/assets/icons';
import { AttentionTriangleIcon, InfoIcon } from '@rsos/base-assets/icons';
import { Box, Button, Text } from '@rsos/base-components';
import { StateWithEmergencyCalls } from '@rsos/reduxDataStore/src/features/emergencyCalls/emergencyCallsSlice';
import { type EventShare } from '@rsos/reduxDataStore/src/features/eventShare';
import {
  useCreateEventShareMutation,
  useGetEventSharesByPhoneQuery,
} from '@rsos/reduxDataStore/src/features/eventShare/eventShareApi';
import { extractErrorMessage } from '@rsos/reduxDataStore/src/utils/errorUtils';
import { useCurrentOrgName } from '@rsos/sinatra/src/selectors';
import {
  notifyDefault,
  notifyError,
  THIRTY_SECONDS,
} from '@rsos/utils/toastUtils';
import { FieldNotes } from '../';
import { getConnectShareUrl } from '../../../../utils/getConnectShareUrl';
import {
  FIELD_DIRECT_RECIPIENTS_SHARE,
  FIELD_SHARE_LINK_SHARE,
  type FieldSharingMethod,
} from '../../constants/shareMethods';
import DataSourcesAndInvites from './DataSourcesAndInvites';
import { LimitReachedWrapper, ShareLinkContainer } from './FieldShare.styles';
import ShareEventRecipients, {
  type MultiInputTag,
} from './ShareEventRecipients';
import ToastMessage from './ToastMessage';
import {
  type FieldFormValues,
  type StepNumber,
  createValidationSchema,
  createInitialValues,
  isStepValid,
  createSubmissionPayload,
} from './helpers';

const FieldShare = ({ onClose }: { onClose: () => void }) => {
  const [currentStep, setCurrentStep] = useState<StepNumber>(1);
  const [sharingMethod, setSharingMethod] = useState<FieldSharingMethod>(
    FIELD_DIRECT_RECIPIENTS_SHARE,
  );
  const [shareResult, setShareResult] = useState<{
    shareLink: string;
    success: boolean;
    shareData?: EventShare;
  } | null>(null);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [allowSubmission, setAllowSubmission] = useState(false);

  const selectedCallerID = useSelector(
    (state: StateWithEmergencyCalls) => state.emergencyCalls.selectedCallerID,
  );

  const [createEventShare] = useCreateEventShareMutation();

  const orgName = useCurrentOrgName();

  // Format the URI from the selected caller ID
  const uri = selectedCallerID ? `tel://${selectedCallerID}` : '';

  // Fetch existing shares for the current phone number
  const { data: existingShares = [] } = useGetEventSharesByPhoneQuery(
    {
      org: orgName || '',
      phoneNumber: uri,
    },
    {
      skip: !orgName || !uri,
    },
  );

  // Count active shares
  const activeSharesCount = useMemo(() => {
    return existingShares.filter(share => share.status === 'active').length;
  }, [existingShares]);

  // Check if sharing limit is reached (3 active shares)
  const isShareLimitReached = activeSharesCount >= 3;

  const validationSchema = useMemo(
    () => createValidationSchema(currentStep, sharingMethod),
    [currentStep, sharingMethod],
  );

  // Memoized initial values to prevent Formik re-initialization
  const initialValues = useMemo(
    () => createInitialValues(sharingMethod),
    [sharingMethod],
  );

  const handleSubmit = async (values: FieldFormValues) => {
    if (!orgName || !uri) {
      notifyError('Organization or caller ID not available');
      return;
    }

    setIsSubmitting(true);
    try {
      const submissionData = createSubmissionPayload(
        values,
        sharingMethod,
        uri,
        orgName,
      );

      const result = await createEventShare({
        org: orgName,
        ...submissionData,
      }).unwrap();

      notifyDefault({
        message: <ToastMessage selectedCallerID={selectedCallerID} />,
        customIcon: InfoIcon,
        options: { autoClose: THIRTY_SECONDS, closeButton: true },
      });

      if (sharingMethod === FIELD_SHARE_LINK_SHARE) {
        const shareKey = result?.recipients?.sender?.share_key || '';
        const shareLink = shareKey ? getConnectShareUrl(shareKey) : '';
        setShareResult({
          shareLink,
          success: true,
          shareData: result,
        });
        setCurrentStep(4);
      } else {
        onClose();
      }
    } catch (error: unknown) {
      const errorMessage = extractErrorMessage(error, 'Failed to create share');
      notifyError(errorMessage);
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleNext = () => {
    if (currentStep < 4) {
      setCurrentStep(prev => (prev + 1) as StepNumber);
    }
  };

  const handleBack = () => {
    if (currentStep > 1) {
      setCurrentStep(prev => (prev - 1) as StepNumber);
    }
  };

  const validateStep = (
    step: StepNumber,
    values: FieldFormValues,
    errors: FormikErrors<FieldFormValues>,
  ) => isStepValid(step, values, errors, sharingMethod);

  const createSharingMethodHandler = useCallback(
    (
      setFieldValue: FormikProps<FieldFormValues>['setFieldValue'],
      validateForm: FormikProps<FieldFormValues>['validateForm'],
    ) =>
      (method: FieldSharingMethod) => {
        setSharingMethod(method);
        setFieldValue('sharingMethod', method);

        // Clear recipient validation errors when switching to share-link
        if (method === FIELD_SHARE_LINK_SHARE) {
          setFieldValue('recipients', []);
        }

        // Trigger validation after React re-render cycle completes
        setTimeout(() => validateForm(), 0);
      },
    [setSharingMethod],
  );

  const handleCopyLink = () => {
    if (shareResult?.shareLink) {
      navigator.clipboard.writeText(shareResult.shareLink);
    }
  };

  return (
    <Formik
      initialValues={initialValues}
      validationSchema={validationSchema}
      onSubmit={values => {
        // Only allow submission if explicitly triggered by user
        if (allowSubmission) {
          setAllowSubmission(false);
          handleSubmit(values);
        }
      }}
      enableReinitialize
    >
      {({ values, setFieldValue, errors, validateForm }) => {
        const handleSharingMethodChange = createSharingMethodHandler(
          setFieldValue,
          validateForm,
        );

        return (
          <Form>
            {isShareLimitReached && currentStep < 4 && (
              <LimitReachedWrapper>
                <AttentionTriangleIcon height={16} width={16} />
                <Text size="normal">
                  Limit reached: Max 3 active shares per event.
                </Text>
              </LimitReachedWrapper>
            )}
            <Box mb="24px">
              {currentStep === 1 && (
                <>
                  <ShareEventRecipients
                    onRecipientsChange={(recipients: MultiInputTag[]) =>
                      setFieldValue('recipients', recipients)
                    }
                    onSharingMethodChange={handleSharingMethodChange}
                    initialRecipients={values.recipients}
                    selectedSharingMethod={sharingMethod}
                    error={
                      typeof errors.recipients === 'string'
                        ? errors.recipients
                        : undefined
                    }
                    isDisabled={isShareLimitReached}
                  />
                </>
              )}

              {currentStep === 2 && (
                <>
                  <DataSourcesAndInvites
                    sources={values.dataSources}
                    onChange={setFieldValue}
                  />
                  {errors.dataSources && (
                    <Text color="lightDanger" size="small" mt="8px">
                      {typeof errors.dataSources === 'string'
                        ? errors.dataSources
                        : 'Please select at least one data source'}
                    </Text>
                  )}
                </>
              )}

              {currentStep === 3 && (
                <FieldNotes
                  value={values.notes}
                  onChange={(e: React.ChangeEvent<HTMLTextAreaElement>) =>
                    setFieldValue('notes', e.target.value)
                  }
                />
              )}

              {currentStep === 4 && shareResult && (
                <Box>
                  <Text size="normal" color="secondaryText">
                    {shareResult.success ? 'Share Link:' : 'Share Failed'}
                  </Text>

                  {shareResult.success && (
                    <>
                      <ShareLinkContainer>
                        <Text size="normal" color="secondaryText" mb="12px">
                          {shareResult.shareLink}
                        </Text>
                      </ShareLinkContainer>
                      <Button
                        btnType="primary"
                        type="button"
                        onClick={handleCopyLink}
                        size="small"
                      >
                        <Box horizontal flow={8} align="center">
                          <CopyIcon />
                          <Text size="normal">Copy Link</Text>
                        </Box>
                      </Button>
                    </>
                  )}
                </Box>
              )}
            </Box>

            <Box horizontal flow={8}>
              {currentStep === 1 ? (
                <>
                  <Button
                    btnType="tertiary"
                    type="button"
                    grow
                    onClick={onClose}
                  >
                    Cancel
                  </Button>
                  <Button
                    btnType="primary"
                    type="button"
                    onClick={handleNext}
                    disabled={
                      !validateStep(1, values, errors) || isShareLimitReached
                    }
                    grow
                  >
                    Next
                  </Button>
                </>
              ) : currentStep === 2 ? (
                <>
                  <Button
                    btnType="tertiary"
                    type="button"
                    onClick={handleBack}
                    grow
                  >
                    Back
                  </Button>
                  <Button
                    btnType="primary"
                    type="button"
                    onClick={handleNext}
                    disabled={!validateStep(2, values, errors)}
                    grow
                  >
                    Next
                  </Button>
                </>
              ) : currentStep === 3 ? (
                <>
                  <Button
                    btnType="tertiary"
                    type="button"
                    onClick={handleBack}
                    grow
                  >
                    Back
                  </Button>
                  <Button
                    btnType="primary"
                    type="submit"
                    grow
                    disabled={isSubmitting || isShareLimitReached}
                    onClick={() => setAllowSubmission(true)}
                  >
                    {isSubmitting ? 'Creating Share Link...' : 'Share'}
                  </Button>
                </>
              ) : (
                <>
                  <Button
                    btnType="tertiary"
                    type="button"
                    onClick={onClose}
                    grow
                  >
                    Close
                  </Button>
                </>
              )}
            </Box>
          </Form>
        );
      }}
    </Formik>
  );
};

export default FieldShare;
